import React, { useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import dxForm from 'devextreme/ui/form';
import Form, { FormAction, FormItem, FormRefType } from '#/common/components/Form';
import useToast from '#/common/hooks/useToast';
import type { AssetType } from '#/graphql/types';
import useSession from '#/common/hooks/useSession';
import { UserRole } from '#/common/types/userTypes';
import { inUseStatuses } from '../AssetType.Mappings';
import { get } from 'lodash';
import allFields from '../../utils/fields';
import { getCleanFormErrors } from '#/common/utils/customRelatedItemsValidation';
import { InitializedEventInfo } from 'devextreme/events';
import { setEditorOptions } from '#/common/components/Form/formItemOption';

export type SubmitAssetType = (
  assetType: AssetType,
) => Promise<{ data?: AssetType; errors?: any[] } | undefined> | undefined;

export interface AssetTypeFormProps {
  assetType: AssetType;
  title: string;
  fields: FormItem<AssetType>[];
  submit?: SubmitAssetType;
  deleteAssetType?: () => unknown;
}

const isFormEditableByRole: Record<UserRole, boolean> = {
  [UserRole.asset_manager]: true,
  [UserRole.trane_admin]: true,
  [UserRole.technician]: false,
};

const AssetTypeForm: React.FC<AssetTypeFormProps> = ({
  assetType,
  fields,
  title,
  submit,
  deleteAssetType,
}) => {
  const { alert, confirm } = useToast();
  const navigate = useNavigate();
  const formRef = useRef<FormRefType | null>(null);

  const { user } = useSession();

  const role = user?.role as UserRole | undefined;

  const isReadonly =
    !role ||
    !isFormEditableByRole[role] ||
    !!(assetType.thirdPartyId && assetType.thirdPartySource);

  const actions = useMemo(() => {
    const result: FormAction<AssetType>[] = [];

    if (
      !isReadonly &&
      deleteAssetType &&
      assetType.inUseStatus === inUseStatuses.newlyCreated.key
    ) {
      result.push({
        label: 'Delete',
        icon: 'trash',
        type: 'danger',
        onClick: async (e) => {
          const sure = await confirm(
            'error',
            `Are you sure you want to delete asset type: ${e.record?.name}?`,
          );

          if (!sure) {
            return;
          }

          e.setIsLoading(true);
          const res = await deleteAssetType();
          e.setIsLoading(false);

          const errors = get(res, 'errors', undefined) as string[] | undefined;

          if (errors) alert('error', 'Could not delete asset type');
          else navigate('/assets/types');
        },
      });
    }

    if (!isReadonly && submit) {
      result.push({
        label: 'Submit',
        type: 'submit',
        onClick: async (e) => {
          if (!e.record) {
            return;
          }
          const validation = e.form?.validate();
          if (!validation?.isValid && getCleanFormErrors(validation, allFields, e.record).length) {
            void alert('error', `${title} is not valid, check field entries.`);
            return;
          }
          if (
            e.record.inUseStatus === inUseStatuses.inUse.key &&
            assetType.inUseStatus !== inUseStatuses.inUse.key
          ) {
            const sure = await confirm(
              'warning',
              'Once set to In-use, this Asset Type cannot be deleted. Do you want to proceed?',
            );
            if (!sure) {
              return;
            }
          }

          try {
            e.setIsLoading(true);
            const result = await submit(e.record);
            e.setIsLoading(false);

            if (result?.errors) {
              void alert('error', result.errors.join(`;\n`));
              return;
            }
            if (!assetType.id && result && result.data?.id) {
              navigate(`/assets/types/${result.data?.id}`, { replace: true });
            }

            void alert('success', 'Changes saved!');
            formRef.current?.repaint();
          } catch (err: any) {
            e.setIsLoading(false);
            console.log('Error', err);
            void alert('error', 'Missing or invalid data!');
          }
        },
      });
    }

    return result;
  }, [alert, confirm, navigate, submit, assetType, deleteAssetType]);

  const onContentReady = useCallback((e: InitializedEventInfo<dxForm>) => {
    const { component: form } = e;
    if (!form) return;

    const formData = form.option('formData');

    if (formData?.inUseStatus !== inUseStatuses.newlyCreated.key) {
      const inUseStatusOptions = {
        dataSource: Object.values({
          ...inUseStatuses,
          newlyCreated: { ...inUseStatuses.newlyCreated, disabled: true },
        }),
      };
      setEditorOptions(form, 'inUseStatus', inUseStatusOptions);
    }
  }, []);

  return (
    <Form
      ref={formRef}
      supportTenant
      record={assetType}
      fields={fields}
      actions={actions}
      onContentReady={onContentReady}
      readonly={isReadonly}
    />
  );
};

export default React.memo(AssetTypeForm);
